import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Package, Plus, Search, Trash2, Edit3, RefreshCw, CheckCircle2, ShoppingCart, Clock, Upload, FileDown, Sparkles, Loader2, BookOpen } from "lucide-react";
import { CsvImportDialog } from "./MaterialsCsvImport";
import { ExtractMaterialsDialog } from "./ExtractMaterialsDialog";
import { MaterialsCataloguePanel } from "./MaterialsCataloguePanel";

const API = `${window.location.origin}/api`;

const STATUS_META = {
  to_order: { label: "To Order", color: "bg-amber-100 text-amber-800", icon: Clock },
  ordered: { label: "Ordered", color: "bg-blue-100 text-blue-800", icon: ShoppingCart },
  received: { label: "Received", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
};

const emptyForm = {
  job_id: "",
  name: "",
  quantity: 1,
  unit: "each",
  unit_cost: 0,
  supplier: "",
  status: "to_order",
  notes: "",
  supplier_links: [],
};

export const MaterialsTab = ({ authHeaders, enquiries = [], onOpenJob }) => {
  const [materials, setMaterials] = useState([]);
  const [summary, setSummary] = useState({ by_status: {}, total_items: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isBulkActing, setIsBulkActing] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCsvOpen, setIsCsvOpen] = useState(false);
  const [isExtractOpen, setIsExtractOpen] = useState(false);
  // Iter 64: default to live-only view. Materials whose linked job is
  // completed/paid/cancelled are hidden from the main list (but preserved in
  // the DB for profitability + "Pull from Job" reuse). Admin can flip on
  // "Show Historical Materials" to audit or re-order historic items.
  const [showHistorical, setShowHistorical] = useState(false);
  const [view, setView] = useState("tracker");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const jobMap = useMemo(() => {
    const m = {};
    enquiries.forEach(e => { m[e.id] = e; });
    return m;
  }, [enquiries]);

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/materials`, authHeaders),
        axios.get(`${API}/admin/materials/summary`, authHeaders),
      ]);
      setMaterials(listRes.data || []);
      setSummary(summaryRes.data || { by_status: {}, total_items: 0 });
    } catch (e) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setIsDialogOpen(true);
  };

  const openEdit = (mat) => {
    setEditing(mat);
    setForm({
      job_id: mat.job_id || "",
      name: mat.name || "",
      quantity: mat.quantity ?? 1,
      unit: mat.unit || "each",
      unit_cost: mat.unit_cost ?? 0,
      supplier: mat.supplier || "",
      status: mat.status || "to_order",
      notes: mat.notes || "",
      supplier_links: mat.supplier_links || [],
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.job_id) { toast.error("Please select a job"); return; }
    if (!form.name?.trim()) { toast.error("Material name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity) || 0,
        unit_cost: Number(form.unit_cost) || 0,
      };
      if (editing) {
        await axios.patch(`${API}/admin/materials/${editing.id}`, payload, authHeaders);
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/admin/materials`, payload, authHeaders);
        toast.success("Material added");
      }
      setIsDialogOpen(false);
      fetchMaterials();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save material");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material entry?")) return;
    try {
      await axios.delete(`${API}/admin/materials/${id}`, authHeaders);
      toast.success("Deleted");
      fetchMaterials();
    } catch (e) {
      toast.error("Failed to delete material");
    }
  };

  const handleStatusChange = async (mat, newStatus) => {
    try {
      await axios.patch(`${API}/admin/materials/${mat.id}`, { status: newStatus }, authHeaders);
      toast.success(`Marked as ${STATUS_META[newStatus]?.label || newStatus}`);
      fetchMaterials();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  // Iter 64: "active" = any job status OTHER than completed/paid/cancelled.
  // Materials with no linked job (e.g. ad-hoc / extracted from historic
  // invoices) are treated as historical by default so they don't clutter the
  // live view.
  const ARCHIVED_JOB_STATUSES = useMemo(() => new Set(['completed', 'paid', 'cancelled']), []);
  const isActiveMaterial = useCallback((m) => {
    const job = jobMap[m.job_id];
    if (!job) return false;  // no linked job → treat as historical
    const st = (job.status || '').toLowerCase();
    return !ARCHIVED_JOB_STATUSES.has(st);
  }, [jobMap, ARCHIVED_JOB_STATUSES]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return materials.filter(m => {
      // Iter 64: live-only default — hide historical unless toggle is on.
      if (!showHistorical && !isActiveMaterial(m)) return false;
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      const job = jobMap[m.job_id];
      if (customerFilter !== "all" && (job?.name || '') !== customerFilter) return false;
      if (supplierFilter !== "all" && (m.supplier || '').trim() !== supplierFilter) return false;
      if (!q) return true;
      const haystack = [m.name, m.supplier, m.notes, job?.name, job?.service, job?.address].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [materials, search, statusFilter, customerFilter, supplierFilter, jobMap, showHistorical, isActiveMaterial]);

  // Count of currently-hidden records so the toggle badge is informative.
  const hiddenHistoricalCount = useMemo(
    () => showHistorical ? 0 : materials.filter(m => !isActiveMaterial(m)).length,
    [materials, showHistorical, isActiveMaterial]
  );

  // Iter 65: Active jobs dropdown + material-name autocomplete.
  // Active = NOT completed/paid/cancelled (mirrors isActiveMaterial logic).
  const activeJobs = useMemo(() => {
    return (enquiries || []).filter(e => !ARCHIVED_JOB_STATUSES.has((e.status || '').toLowerCase()));
  }, [enquiries, ARCHIVED_JOB_STATUSES]);

  // Distinct, trimmed material names — powers the datalist type-ahead so
  // Sandy/Mark get naming consistency (e.g. always "18mm Plywood").
  const materialNameSuggestions = useMemo(() => {
    const seen = new Set();
    for (const m of materials) {
      const n = (m.name || '').trim();
      if (n) seen.add(n);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [materials]);

  // Distinct customers (from enquiry job names that actually have materials)
  // and suppliers — drive the two dropdown filters above the table.
  const customerOptions = useMemo(() => {
    const set = new Set();
    materials.forEach(m => {
      const name = jobMap[m.job_id]?.name;
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials, jobMap]);

  const supplierOptions = useMemo(() => {
    const set = new Set();
    materials.forEach(m => { if ((m.supplier || '').trim()) set.add(m.supplier.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  // When the filter set changes, prune stale selections so we don't act on rows
  // that are no longer visible.
  useEffect(() => {
    setSelectedIds(prev => {
      const visible = new Set(filtered.map(m => m.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const visibleIds = filtered.map(m => m.id);
    setSelectedIds(prev => {
      const allSelected = visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.size === 0) { toast.error('Select at least one material'); return; }
    setIsBulkActing(true);
    try {
      const res = await axios.post(
        `${API}/admin/materials/bulk-status`,
        { material_ids: Array.from(selectedIds), status: newStatus },
        authHeaders,
      );
      toast.success(`Marked ${res.data?.updated ?? selectedIds.size} as ${STATUS_META[newStatus]?.label || newStatus}`);
      setSelectedIds(new Set());
      fetchMaterials();
    } catch (e) {
      toast.error('Bulk update failed');
    } finally {
      setIsBulkActing(false);
    }
  };

  const handleExportSupplierPdf = async () => {
    if (filtered.length === 0) { toast.error('Nothing to export — adjust your filters'); return; }
    setIsExportingPdf(true);
    try {
      // Which supplier to stamp on the PDF — use the active filter when one is
      // applied, otherwise a generic "All suppliers" label.
      const supplierLabel = supplierFilter !== 'all' ? supplierFilter : null;
      const res = await axios.post(
        `${API}/admin/materials/supplier-pdf`,
        { material_ids: filtered.map(m => m.id), supplier: supplierLabel },
        { ...authHeaders, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `supplier-order-${(supplierLabel || 'all').replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Supplier PDF generated');
    } catch (e) {
      toast.error('PDF export failed');
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="materials-tab">
      <div className="flex gap-1 rounded-lg border p-1 w-fit bg-muted/30" data-testid="materials-view-toggle">
        <Button size="sm" variant={view === "tracker" ? "default" : "ghost"} onClick={() => setView("tracker")} data-testid="materials-view-tracker-btn">
          <Package className="h-4 w-4 mr-1" /> Job Tracker
        </Button>
        <Button size="sm" variant={view === "catalogue" ? "default" : "ghost"} onClick={() => setView("catalogue")} data-testid="materials-view-catalogue-btn">
          <BookOpen className="h-4 w-4 mr-1" /> Catalogue
        </Button>
      </div>

      {view === "catalogue" && <MaterialsCataloguePanel authHeaders={authHeaders} />}

      {view === "tracker" && (<>
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
                <Package className="h-5 w-5" /> Materials Management
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={fetchMaterials} data-testid="materials-refresh-btn">
                  <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={() => setIsCsvOpen(true)} data-testid="materials-import-csv-btn">
                  <Upload className="h-4 w-4 mr-1" /> Import CSV
                </Button>
                <Button onClick={openAdd} className="bg-accent hover:bg-accent/90" data-testid="materials-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Material
                </Button>
              </div>
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(["to_order", "ordered", "received"]).map((s) => {
                const meta = STATUS_META[s];
                const row = summary.by_status?.[s] || { count: 0, cost: 0 };
                const Icon = meta.icon;
                return (
                  <div key={s} className="rounded-lg border p-3" data-testid={`materials-summary-${s}`}>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </div>
                    <div className="text-lg font-semibold mt-1">{row.count}</div>
                    <div className="text-xs text-muted-foreground">£{(row.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                );
              })}
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total entries</div>
                <div className="text-lg font-semibold mt-1">{summary.total_items || 0}</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search material, supplier, customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="materials-search"
                />
              </div>
              {/* Iter 64: "Show Historical Materials" toggle */}
              <label
                className="flex items-center gap-2 rounded-md border px-3 h-9 cursor-pointer select-none hover:bg-accent/30"
                data-testid="materials-show-historical-toggle"
                title="Hidden by default: materials linked to completed/paid jobs, or with no linked job. Tick to reveal every material ever bought."
              >
                <Checkbox
                  checked={showHistorical}
                  onCheckedChange={(v) => setShowHistorical(!!v)}
                  data-testid="materials-show-historical-checkbox"
                />
                <span className="text-sm whitespace-nowrap">
                  Show Historical
                  {!showHistorical && hiddenHistoricalCount > 0 && (
                    <span
                      className="ml-1 text-xs text-muted-foreground"
                      data-testid="materials-hidden-count"
                    >
                      ({hiddenHistoricalCount} hidden)
                    </span>
                  )}
                </span>
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="sm:w-40 h-9" data-testid="materials-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="to_order">To Order</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="sm:w-48 h-9" data-testid="materials-customer-filter">
                  <SelectValue placeholder="Filter by Customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customerOptions.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`materials-customer-option-${name}`}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="sm:w-48 h-9" data-testid="materials-supplier-filter">
                  <SelectValue placeholder="Filter by Supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All suppliers</SelectItem>
                  {supplierOptions.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`materials-supplier-option-${name}`}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={handleExportSupplierPdf}
                disabled={isExportingPdf || filtered.length === 0}
                data-testid="materials-export-supplier-pdf-btn"
              >
                <FileDown className="h-4 w-4 mr-1" />
                Export Supplier PDF
              </Button>
              {/* Iter 63+: Hybrid (keyword + AI) extractor. Scans every paid
                  invoice across all years and proposes material-cost lines to
                  back-fill the Materials Tracker. received_date is stamped to
                  the invoice date, so the Apr-6-2026 AI-Analytics cutoff
                  applies automatically. */}
              <Button
                variant="outline"
                size="sm"
                className="h-9 border-amber-300 bg-amber-50 hover:bg-amber-100"
                onClick={() => setIsExtractOpen(true)}
                data-testid="materials-extract-from-invoices-btn"
                title="Scan every paid invoice for material-cost line items and back-fill the tracker"
              >
                <Sparkles className="h-4 w-4 mr-1 text-amber-700" />
                Extract from Invoices
              </Button>
            </div>

            {/* Bulk-action bar — appears only when at least one row is selected. */}
            {selectedIds.size > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 rounded-md border bg-accent/10 px-3 py-2 text-sm"
                data-testid="materials-bulk-action-bar"
              >
                <span className="font-medium" data-testid="materials-selected-count">
                  {selectedIds.size} selected
                </span>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBulkActing}
                  onClick={() => handleBulkStatus('ordered')}
                  data-testid="materials-bulk-mark-ordered-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-1" /> Mark as Ordered
                </Button>
                <Button
                  size="sm"
                  disabled={isBulkActing}
                  onClick={() => handleBulkStatus('received')}
                  data-testid="materials-bulk-mark-received-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as Received
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="materials-bulk-clear-btn"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p>No materials yet</p>
              <p className="text-xs">Add the first item with "Add Material" above.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filtered.length > 0 && filtered.every(m => selectedIds.has(m.id))}
                        onCheckedChange={toggleAllVisible}
                        aria-label="Select all visible"
                        data-testid="materials-select-all"
                      />
                    </TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Job</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => {
                    const job = jobMap[m.job_id];
                    const meta = STATUS_META[m.status] || STATUS_META.to_order;
                    return (
                      <TableRow key={m.id} data-testid={`material-row-${m.id}`} data-selected={selectedIds.has(m.id) ? 'true' : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(m.id)}
                            onCheckedChange={() => toggleOne(m.id)}
                            aria-label={`Select ${m.name}`}
                            data-testid={`material-select-${m.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{m.name}</div>
                          {m.notes && <div className="text-xs text-muted-foreground truncate max-w-xs">{m.notes}</div>}
                        </TableCell>
                        <TableCell>
                          {job ? (
                            <button
                              className="text-primary hover:underline text-sm text-left"
                              onClick={() => onOpenJob && onOpenJob(job)}
                              data-testid={`material-job-link-${m.id}`}
                            >
                              {job.name}
                              <div className="text-xs text-muted-foreground">{job.service}</div>
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Job {m.job_id.slice(0,8)}…</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{m.quantity} {m.unit}</TableCell>
                        <TableCell className="text-sm">
                          <div>£{(m.total_cost || 0).toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">£{(m.unit_cost || 0).toFixed(2)} / {m.unit}</div>
                        </TableCell>
                        <TableCell className="text-sm">{m.supplier || "—"}</TableCell>
                        <TableCell>
                          <Select value={m.status} onValueChange={(v) => handleStatusChange(m, v)}>
                            <SelectTrigger className={`h-8 w-32 ${meta.color} border-0`} data-testid={`material-status-${m.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="to_order">To Order</SelectItem>
                              <SelectItem value="ordered">Ordered</SelectItem>
                              <SelectItem value="received">Received</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`material-edit-${m.id}`}>
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} data-testid={`material-delete-${m.id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg mx-4" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit material" : "Add material"}</DialogTitle>
            <DialogDescription>Log what's needed for a specific job. Costs are manual — they won't flow to invoices automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Job *</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm(p => ({ ...p, job_id: v }))}>
                <SelectTrigger data-testid="material-form-job">
                  <SelectValue placeholder={activeJobs.length === 0 ? "No active jobs — create or reopen one" : `Select a job… (${activeJobs.length} active)`} />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {activeJobs.length === 0 && <SelectItem value="__none__" disabled>No active jobs</SelectItem>}
                  {activeJobs.map(e => (
                    <SelectItem key={e.id} value={e.id} data-testid={`material-form-job-option-${e.id}`}>
                      {(e.name || 'Unnamed')} · {(e.service || 'General')}{e.address ? ` · ${e.address.split(',')[0]}` : ''}
                    </SelectItem>
                  ))}
                  {/* Iter 65: When EDITING, keep the currently-linked job available
                      even if it's now archived/completed — so material edits don't
                      silently break the link. */}
                  {editing && form.job_id && !activeJobs.some(j => j.id === form.job_id) && jobMap[form.job_id] && (
                    <SelectItem key={form.job_id} value={form.job_id} className="italic opacity-70">
                      {(jobMap[form.job_id].name || 'Unnamed')} · {(jobMap[form.job_id].status || 'archived')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {activeJobs.length < enquiries.length && (
                <p className="text-xs text-muted-foreground mt-1">
                  Hiding {enquiries.length - activeJobs.length} completed/paid/cancelled jobs. Reopen one to log materials against it.
                </p>
              )}
            </div>
            <div>
              <Label>Material name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. 18mm birch ply"
                data-testid="material-form-name"
                list="material-name-suggestions"
                autoComplete="off"
              />
              {/* Iter 65: Type-ahead from every distinct material name seen
                  before — keeps naming consistent across users. */}
              <datalist id="material-name-suggestions">
                {materialNameSuggestions.map(n => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Quantity</Label>
                <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm(p => ({ ...p, quantity: e.target.value }))} data-testid="material-form-qty" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm(p => ({ ...p, unit: e.target.value }))} placeholder="each / m / box" data-testid="material-form-unit" />
              </div>
              <div>
                <Label>Unit cost £</Label>
                <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm(p => ({ ...p, unit_cost: e.target.value }))} data-testid="material-form-unit-cost" />
              </div>
            </div>
            <div>
              <Label>Supplier</Label>
              {/* Iter 65+: Mark's feedback — replace plain text box with a
                  searchable dropdown. Implementation: <Input list=…> backed
                  by a <datalist> built from every distinct supplier already
                  in the DB. Native browser behaviour gives type-ahead search
                  AND lets the user free-type a brand-new supplier name
                  ("Add New") with zero extra UI. Matches the Material Name
                  pattern for consistency. */}
              <Input
                value={form.supplier}
                onChange={(e) => setForm(p => ({ ...p, supplier: e.target.value }))}
                placeholder={supplierOptions.length ? `Pick from ${supplierOptions.length} suppliers or type a new one…` : "Travis Perkins, Amazon…"}
                data-testid="material-form-supplier"
                list="material-supplier-suggestions"
                autoComplete="off"
              />
              <datalist id="material-supplier-suggestions">
                {supplierOptions.map(s => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            {/* Supplier price-check links (Item 2 — manual-assist price tracking) */}
            <div className="space-y-1.5" data-testid="material-form-supplier-links">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Supplier price links</Label>
                <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setForm(p => ({ ...p, supplier_links: [...(p.supplier_links || []), { label: "", url: "", last_price: null, last_checked_at: null }] }))} data-testid="material-supplier-link-add">
                  + Add link
                </Button>
              </div>
              {(form.supplier_links || []).map((link, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_2fr_90px_auto] gap-1 items-center">
                  <Input
                    placeholder="Merchant"
                    value={link.label || ""}
                    onChange={(e) => setForm(p => {
                      const next = [...(p.supplier_links || [])];
                      next[idx] = { ...next[idx], label: e.target.value };
                      return { ...p, supplier_links: next };
                    })}
                    className="h-7 text-xs"
                  />
                  <Input
                    placeholder="Product URL"
                    value={link.url || ""}
                    onChange={(e) => setForm(p => {
                      const next = [...(p.supplier_links || [])];
                      next[idx] = { ...next[idx], url: e.target.value };
                      return { ...p, supplier_links: next };
                    })}
                    className="h-7 text-xs"
                    data-testid={`material-supplier-link-url-${idx}`}
                  />
                  <Input
                    type="number" step="0.01"
                    placeholder="£"
                    value={link.last_price ?? ""}
                    onChange={(e) => setForm(p => {
                      const next = [...(p.supplier_links || [])];
                      next[idx] = {
                        ...next[idx],
                        last_price: e.target.value === "" ? null : parseFloat(e.target.value),
                        last_checked_at: new Date().toISOString(),
                      };
                      return { ...p, supplier_links: next };
                    })}
                    className="h-7 text-xs"
                  />
                  <div className="flex gap-1">
                    {link.url && (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary text-xs px-2 py-1 border rounded hover:bg-primary/10" data-testid={`material-supplier-link-check-${idx}`} title="Open product page to check the current price">
                        Check
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, supplier_links: (p.supplier_links || []).filter((_, i) => i !== idx) }))}
                      className="text-destructive text-xs px-1"
                      aria-label="Remove supplier link"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              {(form.supplier_links || []).length === 0 && (
                <p className="text-xs text-muted-foreground">Add Travis Perkins, Amazon, Screwfix etc. links so you can one-click check the latest retail price.</p>
              )}
            </div>

            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="material-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_order">To Order</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} data-testid="material-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="material-form-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Add material"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightweight legend badge row */}
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <Badge key={k} variant="outline" className={v.color}>{v.label}</Badge>
        ))}
      </div>

      {/* CSV Import dialog */}
      <CsvImportDialog
        open={isCsvOpen}
        onClose={() => setIsCsvOpen(false)}
        onImported={() => { setIsCsvOpen(false); fetchMaterials(); }}
        authHeaders={authHeaders}
        enquiries={enquiries}
      />

      {/* Iter 63+: Extract materials from existing paid invoices */}
      <ExtractMaterialsDialog
        open={isExtractOpen}
        onClose={() => setIsExtractOpen(false)}
        onConfirmed={() => { setIsExtractOpen(false); fetchMaterials(); }}
        authHeaders={authHeaders}
      />
      </>)}
    </div>
  );
};

export default MaterialsTab;
